import { Creatable, DashboardCookieSettings } from '@InstanceFactory';
import * as Cookies from 'js-cookie';
import constants from './Constant';

namespace clientStorage {
    export class LayoutState {
        constructor(public panelProportion: number = 0.8, public treeProportion: number = 0.9) {}

        public static of(source: Partial<LayoutState>): LayoutState {
            return _.assign(new LayoutState(), source || {});
        }
    }

    enum EStorageKey {
        projectId,
        globalSettings,
        recentItems,
        layoutState,
        locale
    }

    enum EStorageType {
        cookie,
        localStore
    }

    export class StorageDescriptors<T = any> {
        constructor(
            public name: string,
            public isGlobal: boolean,
            public instanceItem?: Creatable<T>,
            public storageType: EStorageType = EStorageType.localStore
        ) {}
    }

    export const storageKeys = {
        [EStorageKey.projectId]: new StorageDescriptors<Guid>(constants.projectIdParameter, false),
        [EStorageKey.globalSettings]: new StorageDescriptors(constants.globalSettingsParameter, true, DashboardCookieSettings),
        [EStorageKey.recentItems]: new StorageDescriptors(constants.recentItemsParameter, false),
        [EStorageKey.layoutState]: new StorageDescriptors<LayoutState>(constants.layoutSizeParameter, false),
        [EStorageKey.locale]: new StorageDescriptors<string>(constants.localeParameter, false, null, EStorageType.cookie)
    };

    function isLocalStorageAvailable(): boolean {
        try {
            return !!localStorage;
        } catch (e) {
            return false;
        }
    }

    function getKey(descriptor: StorageDescriptors): string {
        return descriptor.isGlobal ? descriptor.name : `${altosoft.appName ?? altosoft.appId}_${descriptor.name}`;
    }

    export function setObject<T>(descriptor: StorageDescriptors<T>): (v: T) => void {
        return (value: T) => {
            if (descriptor.storageType === EStorageType.localStore) {
                isLocalStorageAvailable() && localStorage.setItem(getKey(descriptor), JSON.stringify(value));
            } else {
                Cookies.set(getKey(descriptor), JSON.stringify(value), { expires: 365 });
            }
        };
    }

    export function getObject<T>(descriptor: StorageDescriptors<T>): () => T {
        return () => {
            let value = null;
            if (descriptor.storageType === EStorageType.localStore) {
                if (isLocalStorageAvailable()) {
                    value = localStorage.getItem(getKey(descriptor));
                }
            } else {
                value = Cookies.get(getKey(descriptor));
            }
            const parser = descriptor.instanceItem ? str => descriptor.instanceItem.create(JSON.parse(str)) : JSON.parse;

            return parser(value);
        };
    }

    export const setActiveProjectId = setObject(storageKeys[EStorageKey.projectId]);
    export const getActiveProjectId = getObject(storageKeys[EStorageKey.projectId]);

    export const getLayoutState = () => LayoutState.of(getObject(storageKeys[EStorageKey.layoutState])());
    export const setLayoutState = setObject(storageKeys[EStorageKey.layoutState]);

    export const getLocale = getObject(storageKeys[EStorageKey.locale]);
    export const setLocale = setObject(storageKeys[EStorageKey.locale]);

    export const getGlobalSettings = getObject(storageKeys[EStorageKey.globalSettings]);
    export const setGlobalSettings = setObject(storageKeys[EStorageKey.globalSettings]);

    export function updateGlobalSettings<T extends keyof DashboardCookieSettings, P extends DashboardCookieSettings[T]>(key: T, value: P): void {
        const settings = getGlobalSettings();
        settings[key] = value;
        setGlobalSettings(settings);
    }
}
export default clientStorage;
